/**
 * Product service for managing digital products
 */
import apiClient from './apiClient';

export interface Product {
  id: number;
  title: string;
  description: string;
  price: number;
  images: string[];
  features: string[];
  category: string;
  tags: string[];
  sellerId: string;
  sellerName: string;
  rating: number;
  releaseDate: string;
  likes: number;
  downloads: number;
  badges?: string[]; // Optional badges for featured products
}

/**
 * Get all products
 */
export const getAllProducts = async (): Promise<Product[]> => {
  const response = await apiClient.get('/products');
  return response.data;
};

/**
 * Get a product by ID
 */
export const getProductById = async (id: number): Promise<Product | undefined> => {
  try {
    const response = await apiClient.get(`/products/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product:', error);
    return undefined;
  }
};

/**
 * Search products by query
 * Searches in title, description, and tags
 */
export const searchProducts = async (query: string): Promise<Product[]> => {
  if (!query) return getAllProducts();

  try {
    const response = await apiClient.get(`/products?search=${encodeURIComponent(query)}`);
    return response.data;
  } catch (error) {
    console.error('Error searching products:', error);
    return [];
  }
};

/**
 * Filter products by category
 */
export const filterProductsByCategory = async (category: string): Promise<Product[]> => {
  if (!category) return getAllProducts();

  try {
    const response = await apiClient.get(`/products?category=${encodeURIComponent(category)}`);
    return response.data;
  } catch (error) {
    console.error('Error filtering products:', error);
    return [];
  }
};

/**
 * Add a new product (admin/seller function)
 */
export const addProduct = async (product: Omit<Product, 'id'>): Promise<Product> => {
  const response = await apiClient.post('/products', product);
  return response.data;
};

/**
 * Update an existing product (admin/seller function)
 */
export const updateProduct = async (id: number, updates: Partial<Product>): Promise<Product | null> => {
  try {
    const response = await apiClient.put(`/products/${id}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating product:', error);
    return null;
  }
};

/**
 * Delete a product (admin/seller function)
 */
export const deleteProduct = async (id: number): Promise<boolean> => {
  try {
    const response = await apiClient.delete(`/products/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting product:', error);
    return false;
  }
};

/**
 * Like a product
 */
export const likeProduct = async (id: number): Promise<Product | null> => {
  try {
    const response = await apiClient.patch(`/products/${id}/like`);
    return response.data;
  } catch (error) {
    console.error('Error liking product:', error);
    return null;
  }
};

/**
 * Increment product downloads
 */
export const incrementDownloads = async (id: number): Promise<Product | null> => {
  try {
    const response = await apiClient.patch(`/products/${id}/download`);
    return response.data;
  } catch (error) {
    console.error('Error incrementing downloads:', error);
    return null;
  }
};
